export function setPromoUrl(promoId) {
  const url = new URL(window.location.href);
  if (promoId) url.searchParams.set('promoId', promoId);
  else url.searchParams.delete('promoId');
  window.history.pushState({ promoId: promoId || null }, '', `${url.pathname}${url.search}`);
}

/** Normalize promo categories to ['casino' | 'sport', ...]. Supports dual membership. */
export function getPromoTypes(promo) {
  if (!promo) return [];
  if (Array.isArray(promo.types) && promo.types.length) {
    return normalizePromoTypes(promo.types);
  }
  return normalizePromoTypes(promo.type ?? promo.promo_type);
}

export function normalizePromoTypes(raw) {
  const parts = Array.isArray(raw)
    ? raw
    : String(raw || '')
      .split(/[,|;/\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
  const out = [];
  for (const p of parts) {
    if ((p === 'casino' || p === 'sport' || p === 'sports') && !out.includes(p === 'sports' ? 'sport' : p)) {
      out.push(p === 'sports' ? 'sport' : p);
    }
  }
  // stable order: casino first
  return out.sort((a, b) => {
    if (a === b) return 0;
    if (a === 'casino') return -1;
    if (b === 'casino') return 1;
    return 0;
  });
}

export function serializePromoTypes(types) {
  const list = normalizePromoTypes(types);
  return list.length ? list.join(',') : 'sport';
}

export function promoHasType(promo, type) {
  const want = type === 'sports' ? 'sport' : type;
  return getPromoTypes(promo).includes(want);
}

function typeRank(promo) {
  const types = getPromoTypes(promo);
  if (types.includes('casino')) return 0;
  if (types.includes('sport')) return 1;
  return 2;
}

export function sortPromosCasinoFirst(source) {
  return [...(source || [])].sort((a, b) => {
    const byType = typeRank(a) - typeRank(b);
    if (byType !== 0) return byType;
    return 0;
  });
}

export function groupPromosByCategory(source) {
  const sorted = sortPromosCasinoFirst(source);
  return {
    casino: sorted.filter((p) => promoHasType(p, 'casino')),
    sport: sorted.filter((p) => promoHasType(p, 'sport')),
    other: sorted.filter((p) => getPromoTypes(p).length === 0),
  };
}

export function filterPromos(source, filter) {
  let list = source || [];
  if (filter === 'All' || filter === 'all') {
    return sortPromosCasinoFirst(list);
  }
  if (filter.startsWith('Sport') || filter === 'sport') {
    list = list.filter((p) => promoHasType(p, 'sport'));
  } else if (filter.startsWith('Casino') || filter === 'casino') {
    list = list.filter((p) => promoHasType(p, 'casino'));
  }
  return sortPromosCasinoFirst(list);
}

export const WELCOME_PROMO_ID = 'TR_CRM_NC_welcome_freebet100_freespins300';

export function isTournamentPromo(promo) {
  if (!promo) return false;
  if (promo.kind === 'tournament') return true;
  if (promo.name || promo.prizeMain || promo.places || promo.participants) return true;
  const caption = String(promo.caption || promo.uptitle || '').toLowerCase();
  return caption.includes('tournament') || caption.includes('turnuva');
}

export function getPromoModalType(promo) {
  if (!promo) return 'details';
  if (isTournamentPromo(promo)) return 'tournament';
  // welcome and all action landings use the same Fonbet details modal
  if (promo.modalType === 'welcome-landing') return 'details';
  if (promo.id === 'welcome' || promo.promoId === WELCOME_PROMO_ID) return 'details';
  return 'details';
}

export function hasPromoLandingContent(promo) {
  if (!promo) return false;
  if (getPromoPageHtml(promo)) return true;
  if (Array.isArray(promo.terms) && promo.terms.length) return true;
  if (promo.detailTitle || promo.detailSubtitle) return true;
  if (promo.title || promo.sub || promo.subtitle || promo.caption) return true;
  if (promo.ctaLabel || promo.cta) return true;
  return false;
}

export function getPromoLink(promo) {
  if (!promo) return '';
  return String(promo.link || promo.linkUrl || '').trim();
}

export function getPromoPageHtml(promo) {
  if (!promo) return '';
  return String(promo.pageHtml || promo.page_html || promo.rulesHtml || '').trim();
}

export function formatPromoTimeLeft(promo, tr) {
  const custom = String(promo?.timer || '').trim();
  if (custom) return custom;
  const endRaw = promo?.endsAt || promo?.ends_at;
  if (!endRaw) return '';
  const end = new Date(endRaw);
  if (Number.isNaN(end.getTime())) return '';
  const diffMs = end.getTime() - Date.now();
  if (diffMs <= 0) return tr ? tr('promos.timer.ended') : '';
  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  if (days >= 1) {
    return tr
      ? tr('promos.timer.daysLeft', { count: days })
      : `Kalan: ${days} gün`;
  }
  if (hours >= 1) {
    return tr
      ? tr('promos.timer.hoursLeft', { count: hours })
      : `Kalan: ${hours} saat`;
  }
  const mins = Math.max(1, Math.floor((diffMs % 3600000) / 60000));
  return tr
    ? tr('promos.timer.minutesLeft', { count: mins })
    : `Kalan: ${mins} dk`;
}

export function resolvePromoClick(promo, openModal) {
  if (!promo) return false;
  if (getPromoModalType(promo) === 'tournament') {
    openModal?.(promo);
    return true;
  }

  // Prefer Fonbet-style landing modal whenever there is content
  if (hasPromoLandingContent(promo)) {
    openModal?.(promo);
    return true;
  }

  const link = getPromoLink(promo);
  if (link) {
    if (/^https?:\/\//i.test(link)) {
      window.open(link, '_blank', 'noopener,noreferrer');
      return true;
    }
    if (link.startsWith('//')) {
      window.open(`https:${link}`, '_blank', 'noopener,noreferrer');
      return true;
    }
    if (link.startsWith('/')) {
      window.history.pushState({}, '', link);
      window.dispatchEvent(new PopStateEvent('popstate'));
      return true;
    }
    window.location.href = link;
    return true;
  }

  return false;
}
